import React, { useState } from 'react';
import { FaShoppingBag, FaTag, FaStar, FaArrowLeft, FaShoppingCart, FaPlus, FaMinus, FaCheckCircle } from 'react-icons/fa';
import { motion } from 'framer-motion';

const Market = () => {
    const [view, setView] = useState('list'); // 'list', 'details', 'cart'
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [cart, setCart] = useState([]);

    const products = [
        {
            id: 1,
            name: "Premium Wedding Garland",
            price: 1500,
            rating: 4.5,
            image: "https://images.unsplash.com/photo-1595981267035-7b04ca84a82d?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60",
            category: "Accessories",
            description: "Handcrafted fresh flower garland made with premium roses and jasmine. Perfect for your special day."
        },
        {
            id: 2,
            name: "Designer Bridal Saree",
            price: 15000,
            rating: 4.8,
            image: "https://images.unsplash.com/photo-1610030469983-98e550d6193c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60",
            category: "Clothing",
            description: "Exquisite Kanchipuram silk saree with intricate gold zari work. A masterpiece for the bride."
        },
        {
            id: 3,
            name: "Gold Plated Necklace Set",
            price: 4500,
            rating: 4.9,
            image: "https://images.unsplash.com/photo-1599643478518-17488fbbcd75?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60",
            category: "Jewelry",
            description: "Stunning gold-plated necklace set with matching earrings. Adds a touch of elegance to any outfit."
        },
        {
            id: 4,
            name: "Groom's Royal Sherwani",
            price: 12000,
            rating: 4.6,
            image: "https://images.unsplash.com/photo-1597983073493-88cd35cf93b0?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60",
            category: "Clothing",
            description: "Regal sherwani for the groom, featuring intricate embroidery and a comfortable fit."
        },
        {
            id: 5,
            name: "Wedding Photography Package",
            price: 50000,
            rating: 4.7,
            image: "https://images.unsplash.com/photo-1537633552985-df8429e8048b?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60",
            category: "Services",
            description: "Comprehensive photography package including pre-wedding shoot, wedding day coverage, and a premium album."
        },
        {
            id: 6,
            name: "Professional Makeup Artist",
            price: 8000,
            rating: 4.5,
            image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=60",
            category: "Services",
            description: "Expert bridal makeup services using high-end international brands. Includes hair styling and draping."
        }
    ];

    const addToCart = (product) => {
        const existingItem = cart.find(item => item.id === product.id);
        if (existingItem) {
            setCart(cart.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item));
        } else {
            setCart([...cart, { ...product, quantity: 1 }]);
        }
        alert(`${product.name} added to cart!`);
    };

    const removeFromCart = (productId) => {
        setCart(cart.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, delta) => {
        setCart(cart.map(item => {
            if (item.id === productId) {
                const newQuantity = Math.max(1, item.quantity + delta);
                return { ...item, quantity: newQuantity };
            }
            return item;
        }));
    };

    const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);

    const handleCheckout = () => {
        alert("Proceeding to payment gateway... (Simulation)");
        setCart([]);
        setView('list');
    };

    return (
        <div className="max-w-7xl mx-auto min-h-full">
            {/* Header */}
            <div className="flex justify-between items-center mb-8 px-6 pt-6">
                <div className="flex items-center gap-4">
                    {view !== 'list' && (
                        <button onClick={() => setView('list')} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                            <FaArrowLeft />
                        </button>
                    )}
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <FaShoppingBag className="text-rose-600" /> Wedding Market
                        </h2>
                        {view === 'list' && <p className="text-gray-500 mt-1 text-sm hidden md:block">Everything you need for your perfect wedding.</p>}
                    </div>
                </div>
                <button
                    onClick={() => setView('cart')}
                    className="relative bg-white p-3 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all"
                >
                    <FaShoppingCart className="text-gray-700 text-xl" />
                    {cart.length > 0 && (
                        <span className="absolute -top-2 -right-2 bg-rose-600 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center border-2 border-white">
                            {cart.reduce((acc, item) => acc + item.quantity, 0)}
                        </span>
                    )}
                </button>
            </div>

            {/* Content */}
            {view === 'list' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 px-6 pb-6">
                    {products.map(product => (
                        <motion.div
                            key={product.id}
                            whileHover={{ y: -5 }}
                            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
                            onClick={() => { setSelectedProduct(product); setView('details'); }}
                        >
                            <div className="relative h-56 overflow-hidden">
                                <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />
                                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-gray-800 shadow-sm">
                                    {product.category}
                                </div>
                            </div>
                            <div className="p-5">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-lg text-gray-800 line-clamp-1">{product.name}</h3>
                                    <div className="flex items-center gap-1 text-amber-500 text-sm font-bold bg-amber-50 px-2 py-1 rounded-lg">
                                        <FaStar /> {product.rating}
                                    </div>
                                </div>
                                <p className="text-gray-500 text-sm line-clamp-2 mb-4">{product.description}</p>
                                <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-50">
                                    <div className="text-xl font-black text-gray-900">₹{product.price.toLocaleString()}</div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); addToCart(product); }}
                                        className="px-4 py-2 bg-rose-50 text-rose-600 font-bold rounded-lg hover:bg-rose-600 hover:text-white transition-all flex items-center gap-2"
                                    >
                                        <FaPlus className="text-xs" /> Add
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {view === 'details' && selectedProduct && (
                <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden mx-6 mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2">
                        <div className="h-64 md:h-auto relative">
                            <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-8 md:p-12 flex flex-col justify-center">
                            <div className="mb-6">
                                <span className="text-rose-600 font-bold text-sm tracking-wider uppercase">{selectedProduct.category}</span>
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">{selectedProduct.name}</h2>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="flex items-center gap-1 text-amber-500 font-bold text-lg">
                                        <FaStar /> {selectedProduct.rating}
                                    </div>
                                    <span className="text-gray-300">|</span>
                                    <span className="text-gray-500">124 Reviews</span>
                                </div>
                                <p className="text-gray-600 text-lg leading-relaxed">{selectedProduct.description}</p>
                            </div>

                            <div className="mt-auto pt-8 border-t border-gray-100">
                                <div className="flex items-center justify-between mb-8">
                                    <span className="text-gray-500">Price</span>
                                    <span className="text-4xl font-black text-gray-900">₹{selectedProduct.price.toLocaleString()}</span>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <button
                                        onClick={() => addToCart(selectedProduct)}
                                        className="py-4 bg-gray-100 text-gray-800 font-bold rounded-xl hover:bg-gray-200 transition-colors"
                                    >
                                        Add to Cart
                                    </button>
                                    <button
                                        onClick={() => { addToCart(selectedProduct); setView('cart'); }}
                                        className="py-4 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-colors shadow-lg shadow-rose-200"
                                    >
                                        Buy Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {view === 'cart' && (
                <div className="max-w-4xl mx-auto px-6 pb-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">Your Shopping Cart</h3>
                    {cart.length === 0 ? (
                        <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-gray-300">
                            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-300 text-3xl">
                                <FaShoppingCart />
                            </div>
                            <h4 className="text-xl font-bold text-gray-800 mb-2">Your cart is empty</h4>
                            <p className="text-gray-500 mb-8">Looks like you haven't added anything yet.</p>
                            <button onClick={() => setView('list')} className="px-8 py-3 bg-rose-600 text-white font-bold rounded-xl hover:bg-rose-700 transition-colors">
                                Start Shopping
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            <div className="lg:col-span-2 space-y-4">
                                {cart.map(item => (
                                    <div key={item.id} className="bg-white p-4 rounded-2xl border border-gray-200 flex gap-4 items-center">
                                        <img src={item.image} alt={item.name} className="w-20 h-20 rounded-xl object-cover" />
                                        <div className="flex-1">
                                            <h4 className="font-bold text-gray-800">{item.name}</h4>
                                            <p className="text-rose-600 font-bold">₹{item.price.toLocaleString()}</p>
                                        </div>
                                        <div className="flex items-center gap-3 bg-gray-50 rounded-lg p-1">
                                            <button onClick={() => updateQuantity(item.id, -1)} className="p-2 hover:bg-white rounded-md shadow-sm transition-all text-xs"><FaMinus /></button>
                                            <span className="font-bold w-4 text-center">{item.quantity}</span>
                                            <button onClick={() => updateQuantity(item.id, 1)} className="p-2 hover:bg-white rounded-md shadow-sm transition-all text-xs"><FaPlus /></button>
                                        </div>
                                        <button onClick={() => removeFromCart(item.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                                            <FaShoppingCart />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <div className="lg:col-span-1">
                                <div className="bg-white p-6 rounded-2xl border border-gray-200 sticky top-24">
                                    <h4 className="font-bold text-gray-800 mb-4 text-lg">Order Summary</h4>
                                    <div className="space-y-3 mb-6">
                                        <div className="flex justify-between text-gray-600">
                                            <span>Subtotal</span>
                                            <span>₹{cartTotal.toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between text-gray-600">
                                            <span>Shipping</span>
                                            <span className="text-green-600 font-bold">Free</span>
                                        </div>
                                        <div className="flex justify-between text-gray-600">
                                            <span>Tax (18%)</span>
                                            <span>₹{(cartTotal * 0.18).toLocaleString()}</span>
                                        </div>
                                        <div className="border-t border-gray-100 pt-3 flex justify-between font-bold text-lg text-gray-900">
                                            <span>Total</span>
                                            <span>₹{(cartTotal * 1.18).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCheckout}
                                        className="w-full py-4 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <FaCheckCircle /> Checkout
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Market;
